import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import Breadcrumb from '@/components/base/Breadcrumb';
import { news } from '@/mocks/news';
import { usePageSeo } from '@/hooks/usePageSeo';

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const item = news.find((n) => String(n.id) === id);

  usePageSeo({
    title: item ? `${item.title} — 小仓优子最新新闻` : '资讯未找到 — 小仓优子最新动态',
    description: item
      ? `${item.summary} 阅读本页了解小仓优子相关的最新动态与详细报道。`
      : '未找到对应的新闻资讯。',
    keywords: item ? `小仓优子,${item.category},最新动态,娱乐资讯` : '小仓优子,最新动态',
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!item) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <SiteHeader />
        <main className="flex-1 flex flex-col items-center justify-center px-4 py-24 text-center">
          <span className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center text-foreground-300">
            <i className="ri-error-warning-line text-3xl"></i>
          </span>
          <h1 className="font-heading text-xl md:text-2xl text-foreground-950 mt-5">未找到该资讯</h1>
          <p className="text-sm text-foreground-600 mt-3">该资讯可能已被移除，或链接有误。</p>
          <Link
            to="/category/news"
            className="mt-6 px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            返回新闻动态列表
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const paragraphs = item.content.split('\n').filter((p) => p.trim().length > 0);
  const related = news.filter((n) => n.id !== item.id).slice(0, 4);

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <section className="w-full bg-gradient-to-br from-accent-50 via-background-50 to-secondary-50 border-b border-background-200">
          <div className="w-full px-4 md:px-8 py-8 md:py-12">
            <Breadcrumb
              items={[{ label: '首页', to: '/' }, { label: '新闻动态', to: '/category/news' }, { label: item.category }]}
            />
            <div className="flex items-center gap-2 flex-wrap mt-5">
              <span className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs">{item.category}</span>
              <span className="text-sm text-foreground-500">{item.date}</span>
              <span className="text-sm text-foreground-400 flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {item.views.toLocaleString()} 阅读
              </span>
            </div>
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl text-foreground-950 mt-4 max-w-4xl leading-snug">
              {item.title}
            </h1>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-10 md:py-14">
          <div className="grid lg:grid-cols-3 gap-10 lg:gap-14">
            <article className="lg:col-span-2">
              <div className="w-full aspect-[16/9] rounded-lg overflow-hidden border border-background-200 bg-background-100">
                <img
                  src={item.thumbnail}
                  alt={`${item.title} 现场图片`}
                  title={`${item.title} 小仓优子最新新闻配图`}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div className="mt-8 space-y-5">
                <p className="text-base text-foreground-800 leading-loose font-medium">{item.summary}</p>
                {paragraphs.map((p, index) => (
                  <p key={index} className="text-sm md:text-base text-foreground-700 leading-loose">
                    {p}
                  </p>
                ))}
              </div>

              <div className="mt-10 pt-6 border-t border-background-200">
                <Link
                  to="/category/news"
                  className="inline-flex items-center gap-1 text-sm text-accent-600 hover:text-accent-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回新闻列表
                </Link>
              </div>
            </article>

            <aside className="lg:col-span-1">
              <div className="p-5 rounded-lg bg-background-100 border border-background-200">
                <h2 className="font-heading text-lg text-foreground-950 mb-4">相关动态</h2>
                <div className="flex flex-col divide-y divide-background-200">
                  {related.map((n) => (
                    <Link
                      key={n.id}
                      to={`/detail/news/${n.id}`}
                      className="group py-4 first:pt-0 last:pb-0 cursor-pointer"
                    >
                      <h3 className="text-sm text-foreground-800 group-hover:text-accent-600 transition-colors line-clamp-2">
                        {n.title}
                      </h3>
                      <span className="text-xs text-foreground-500 mt-1.5 block">{n.date}</span>
                    </Link>
                  ))}
                </div>
              </div>

              <Link
                to="/profile"
                className="mt-5 flex items-center justify-between p-5 rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
              >
                <span className="text-sm">了解小仓优子的完整资料</span>
                <i className="ri-arrow-right-line"></i>
              </Link>
            </aside>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}